import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function GoogleCallbackPage() {
  const { handleGoogleCallback, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [codeProcessed, setCodeProcessed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle code processing only once
  useEffect(() => {
    // Skip if we've already processed the code
    if (codeProcessed) return;

    const processCode = async () => {
      // Extract the authorization code from URL query parameters
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');

      if (!code) {
        setError('No authorization code found in the URL.');
        setLoading(false);
        return;
      }
      
      setCodeProcessed(true);

      try {
        await handleGoogleCallback(code);
        setLoading(false);
      } catch (err) {
        console.error('Error during Google authentication:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setLoading(false);
      }
    };

    processCode();
  }, [handleGoogleCallback, location.search, codeProcessed]);

  // Handle redirection after authentication
  useEffect(() => {
    if (isAuthenticated && !loading && codeProcessed) {
      const returnPath = sessionStorage.getItem('authReturnPath') || '/';
      sessionStorage.removeItem('authReturnPath');
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, codeProcessed]);

  if (loading && !codeProcessed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-4">Processing authentication...</h1>
          <p className="text-gray-600">Please wait while we complete your sign in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
        <div className="w-full max-w-md p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 animate-fade-in">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-red-100 dark:bg-red-900/30">
              <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Authentication Error</h1>
            <p className="mb-8 text-gray-600 dark:text-gray-300">{error}</p>
            <Button 
              className="w-full"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Return to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state while waiting for auth state to update and redirect
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-4">Completing authentication...</h1>
        <p className="text-gray-600">Setting up your session...</p>
      </div>
    </div>
  );
} 